import React, { useState } from "react";
import Drawer from "../../components/drawer/drawer";
import Modal from "../../components/modal/modal";
import PageHeader from "../../components/page-header/page-header";
import { usePagination } from "../../hooks/use-pagination";
import { FAQ } from "../../types";
import { FaqManagementState } from "./types/faq-types";
import { FaqForm } from "./components/faq-form";
import { FaqTable } from "./components/faq-table";
import { useFaqs } from "./hooks/use-faqs";
import {
  resetFaqState,
  openEditDrawer,
  openCreateDrawer,
  closeDrawer,
  openDeleteModal,
  closeDeleteModal,
  getDrawerTitle,
  getFaqQuestion,
} from "./utils/faq-utils";
import "./faq.css";
import { toast } from "sonner";

const Faq: React.FC = () => {
  const [state, setState] = useState<FaqManagementState>(resetFaqState());

  const { faqs, isLoading, handleCreateFaq, handleUpdateFaq, handleDeleteFaq } =
    useFaqs();

  const {
    currentPage,
    itemsPerPage,
    paginatedItems,
    totalPages,
    handlePageChange,
    handleItemsPerPageChange,
  } = usePagination(faqs);

  const handleSubmit = async (values: Partial<FAQ>): Promise<void> => {
    let response: any;
    if (state.editingFaq) {
      response = await handleUpdateFaq(state.editingFaq.id, values);
    } else {
      response = await handleCreateFaq(values);
    }

    const hasSuccessField = typeof response?.success === "boolean";
    if (!hasSuccessField || response?.success) {
      toast.success(response?.message || "Операция выполнена успешно");
      closeDrawer(setState);
    } else {
      toast.error(response?.message || "Произошла ошибка");
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!state.deletingFaq) return;

    const success = await handleDeleteFaq(state.deletingFaq.id);
    if (success) {
      closeDeleteModal(setState);
    }
  };

  const handleEdit = (faq: FAQ): void => {
    openEditDrawer(setState, faq);
  };

  const handleCreate = (): void => {
    openCreateDrawer(setState);
  };

  const handleCancel = (): void => {
    closeDrawer(setState);
  };

  const handleDeleteClick = (faq: FAQ): void => {
    openDeleteModal(setState, faq);
  };

  const handleDeleteCancel = (): void => {
    closeDeleteModal(setState);
  };

  return (
    <div className="faq-page">
      <PageHeader
        title="FAQ"
        subtitle="Управление часто задаваемыми вопросами"
        actionButton={{
          text: "Добавить FAQ",
          icon: "plus",
          onClick: handleCreate,
        }}
      />

      <FaqTable
        faqs={paginatedItems}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      <Drawer
        title={getDrawerTitle(state.editingFaq)}
        isOpen={state.isDrawerOpen}
        onClose={handleCancel}
        width={600}
      >
        <FaqForm
          initialValues={state.editingFaq}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={isLoading}
        />
      </Drawer>

      <Modal
        isOpen={state.isDeleteModalOpen}
        onClose={handleDeleteCancel}
        title="Вы уверены, что хотите удалить этот FAQ?"
        onConfirm={handleDelete}
      >
        <p>Вы уверены, что хотите удалить этот FAQ?</p>
        <p>
          <strong>
            {state.deletingFaq ? getFaqQuestion(state.deletingFaq) : ""}
          </strong>
        </p>
      </Modal>
    </div>
  );
};

export default Faq;
